import { useEffect, useState } from "react";
import { featuredSlides, hotRankings } from "@/mocks/homeData";
import { CatSolid, Shell } from "./ui";

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const total = featuredSlides.length;

  useEffect(() => {
    const timer = window.setInterval(() => setIdx((i) => (i + 1) % total), 7000);
    return () => window.clearInterval(timer);
  }, [total]);

  const go = (d: number) => setIdx((i) => (i + d + total) % total);
  const slide = featuredSlides[idx];

  return (
    <section className="bg-background-100/60">
      <Shell>
        <div className="flex flex-col gap-5 py-6 lg:flex-row lg:py-8">
          {/* 左侧封面焦点 */}
          <div className="flex w-full flex-col gap-5 lg:w-3/5 xl:w-[62%]">
            <div className="relative overflow-hidden rounded-2xl">
              {/* 封面图 */}
              <div className="relative h-[300px] w-full md:h-[420px]">
                <img
                  src={slide.src}
                  alt={slide.title}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/25 to-foreground-950/5" />
                {/* 覆盖标签 */}
                <div className="absolute left-4 top-4 flex flex-wrap items-center gap-2 md:left-6 md:top-6">
                  <CatSolid cat={slide.area.split("·")[0].trim()} />
                  <span className="inline-flex items-center whitespace-nowrap rounded border border-background-50/40 bg-background-950/30 px-2 py-0.5 text-xs font-semibold text-background-50 backdrop-blur">
                    <i className="ri-star-fill mr-1 text-accent-500" />
                    {slide.flag}
                  </span>
                </div>
              </div>
              {/* 文案 */}
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-7">
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-background-100/90">
                  <span className="inline-flex items-center gap-1"><i className="ri-focus-3-line text-accent-500" />{slide.area}</span>
                  <span className="text-background-100/40">|</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-time-line" />{slide.time}</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-eye-line" />{slide.views}</span>
                </div>
                <h1 className="max-w-[760px] font-heading text-xl font-extrabold leading-snug text-background-50 md:text-[28px] md:leading-tight">
                  {slide.title}
                </h1>
                <p className="mt-2 hidden max-w-2xl text-sm leading-relaxed text-background-100/90 sm:block md:line-clamp-2">
                  {slide.summary}
                </p>
              </div>
              {/* 切换控制 */}
              <div className="absolute right-4 top-4 flex items-center gap-2 md:right-6 md:top-6">
                <button aria-label="上一条" onClick={() => go(-1)} className="grid h-9 w-9 place-items-center rounded-full border border-background-50/40 bg-background-950/20 text-background-50 backdrop-blur transition hover:bg-primary-500">
                  <i className="ri-arrow-left-line" />
                </button>
                <button aria-label="下一条" onClick={() => go(1)} className="grid h-9 w-9 place-items-center rounded-full border border-background-50/40 bg-background-950/20 text-background-50 backdrop-blur transition hover:bg-primary-500">
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
              {/* 圆点 */}
              <div className="absolute bottom-3 right-4 flex gap-1.5 md:right-6">
                {featuredSlides.map((_, i) => (
                  <button
                    key={i}
                    aria-label={`第 ${i + 1} 张`}
                    onClick={() => setIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-accent-500" : "w-2 bg-background-50/50"}`}
                  />
                ))}
              </div>
            </div>

            {/* 次焦点头条三连 */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { icon: "ri-refresh-line", t: "三连反转", d: "同一事件三轮口径,第3条实时更新", c: "text-primary-700 bg-primary-100" },
                { icon: "ri-error-warning-line", t: "24h 热榜", d: "飙升最快的争议与实锤现场", c: "text-accent-800 bg-accent-100" },
                { icon: "ri-file-shield-line", t: "待核验", d: "仍处存疑、等待回应的真假线索", c: "text-secondary-800 bg-secondary-100" },
              ].map((b) => (
                <a
                  key={b.t}
                  href="#break"
                  className="group flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 p-3.5 transition hover:border-primary-300"
                >
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${b.c}`}>
                    <i className={`${b.icon} text-lg`} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-foreground-950">{b.t}</span>
                    <span className="mt-0.5 block truncate text-xs text-foreground-500">{b.d}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* 右侧全网热搜榜 */}
          <aside className="w-full rounded-2xl border border-background-200 bg-background-50 p-5 lg:w-2/5 xl:w-[38%]">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-heading text-lg font-extrabold text-foreground-950">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary-500 text-background-50"><i className="ri-fire-fill" /></span>
                全网热度榜
              </h3>
              <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                <span className="relative flex h-1.5 w-1.5"><span className="absolute h-full w-full animate-ping rounded-full bg-accent-500 opacity-60" /><span className="relative h-1.5 w-1.5 rounded-full bg-accent-500" /></span>
                每10分钟更新
              </span>
            </div>
            <ol className="flex flex-col gap-1">
              {hotRankings.map((r, i) => (
                <li key={r.rank}>
                  <a href="#top" className="group flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-background-100">
                    <span
                      className={`grid h-6 w-6 shrink-0 place-items-center rounded font-heading text-xs font-extrabold ${
                        i === 0
                          ? "bg-primary-500 text-background-50"
                          : i === 1
                            ? "bg-accent-500 text-foreground-950"
                            : i === 2
                              ? "bg-secondary-200 text-secondary-900"
                              : "text-foreground-400"
                      }`}
                    >
                      {r.rank}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground-800 group-hover:text-primary-700">
                        {r.title}
                      </span>
                    </span>
                    <span
                      className={`hidden shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold sm:inline ${
                        r.delta === "新"
                          ? "bg-primary-100 text-primary-700"
                          : r.delta.startsWith("↑")
                            ? "bg-primary-50 text-primary-700"
                            : "bg-background-100 text-foreground-500"
                      }`}
                    >
                      {r.delta === "新" ? "新上榜" : r.delta}
                    </span>
                    <span className="hidden shrink-0 text-xs font-semibold text-accent-700 md:inline">{r.heat}</span>
                  </a>
                </li>
              ))}
            </ol>
            <a
              href="#follow"
              className="mt-3 flex items-center justify-center gap-1 rounded-md border border-dashed border-primary-300 bg-primary-50/60 px-3 py-2 text-xs font-semibold text-primary-700 transition hover:bg-primary-100"
            >
              查看完整榜单与事件后续
              <i className="ri-arrow-right-line" />
            </a>
          </aside>
        </div>
      </Shell>
    </section>
  );
}